import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  CloneSrpRequest,
  CloneSrpResponse,
  CreateSrpRequest,
  CreateSrpResponse,
  DryRunSrpRequest,
  DryRunSrpResponse,
  EccAgenda,
  EccAgendaSubida,
  EccArea,
  EccCallDownloadRequest,
  EccCallJob,
  EccCallJobList,
  EccCallJobRef,
  EccCallSearchRequest,
  EccCdrPreview,
  EccFollowupRequest,
  EccFollowupResponse,
  EccHubStats,
  EccRunDetail,
  EccRunList,
  EccSrpList,
  EccStartRunRequest,
  EccStartRunResponse,
  SrpDetail,
  UpdateSrpRequest,
} from "@/api/types";

const POLL_MS = 3_000;

export function useEccHubStats(area: EccArea) {
  return useQuery({
    queryKey: ["ecc", "hub", area],
    queryFn: ({ signal }) =>
      api<EccHubStats>(`/api/v1/ecc/${area}/stats`, { signal }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useEccCdrPreview(
  area: EccArea,
  periodStart: string,
  periodEnd: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["ecc", "cdr", area, periodStart, periodEnd],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        area,
        period_start: periodStart,
        period_end: periodEnd,
      });
      return api<EccCdrPreview>(`/api/v1/ecc/cdr/preview?${params}`, { signal });
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useEccSrps(area: EccArea) {
  return useQuery({
    queryKey: ["ecc", "srps", area],
    queryFn: ({ signal }) =>
      api<EccSrpList>(`/api/v1/ecc/${area}/srps`, { signal }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useEccAgenda(cliente: string | null | undefined) {
  return useQuery({
    queryKey: ["ecc", "agenda", cliente],
    queryFn: ({ signal }) =>
      api<EccAgenda>(`/api/v1/ecc/agenda/${cliente}`, { signal }),
    enabled: Boolean(cliente),
    staleTime: 30_000,
  });
}

/** Sube el concentrado de las tablas de escalación y reemplaza la agenda. */
export function useEccSubirAgenda(cliente: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contenido: string) =>
      api<EccAgendaSubida>(`/api/v1/ecc/agenda/${cliente}/csv`, {
        method: "POST",
        body: { contenido },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "agenda", cliente] });
    },
  });
}

export function useEccRuns(area: EccArea, page = 1, perPage = 20) {
  return useQuery({
    queryKey: ["ecc", "runs", area, page],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        area,
        page: String(page),
        per_page: String(perPage),
      });
      return api<EccRunList>(`/api/v1/ecc/runs?${params}`, { signal });
    },
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useEccRun(runId: string | null) {
  return useQuery({
    queryKey: ["ecc", "run", runId],
    queryFn: ({ signal }) =>
      api<EccRunDetail>(`/api/v1/ecc/runs/${runId}`, { signal }),
    enabled: !!runId,
    refetchInterval: (query) => {
      const data = query.state.data as EccRunDetail | undefined;
      if (!data) return POLL_MS;
      const terminal: string[] = ["done", "failed"];
      return terminal.includes(data.status) ? false : POLL_MS;
    },
  });
}

export function useEccStartRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EccStartRunRequest) =>
      api<EccStartRunResponse>("/api/v1/ecc/runs", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc"] });
    },
  });
}

export function useEccApprove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (srpId: string) =>
      api<void>(`/api/v1/ecc/srps/${srpId}/approve`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
    },
  });
}

export function useEccReject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ srpId, comment }: { srpId: string; comment: string }) =>
      api<void>(`/api/v1/ecc/srps/${srpId}/reject`, {
        method: "POST",
        body: { comment },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
    },
  });
}

export function useDeleteSrp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (srpId: string) =>
      api<void>(`/api/v1/ecc/srps/${srpId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
      void queryClient.invalidateQueries({ queryKey: ["ecc", "hub"] });
    },
  });
}

export function useDeleteRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api<void>(`/api/v1/ecc/runs/${runId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "runs"] });
      void queryClient.invalidateQueries({ queryKey: ["ecc", "hub"] });
    },
  });
}

export function useBulkDeleteRuns() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ deleted: number }>("/api/v1/ecc/runs/bulk", { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc"] });
    },
  });
}

export function useEccFollowup(parentRunId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EccFollowupRequest) =>
      api<EccFollowupResponse>(`/api/v1/ecc/runs/${parentRunId}/followup`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc"] });
    },
  });
}

// ---- ADR_006: Portal de creación de SRPs ----

export function useSrpDetail(srpId: string | null) {
  return useQuery({
    queryKey: ["ecc", "srp", srpId],
    queryFn: ({ signal }) =>
      api<SrpDetail>(`/api/v1/ecc/srps/${srpId}`, { signal }),
    enabled: !!srpId,
    staleTime: 30_000,
  });
}

export function useCreateSrp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSrpRequest) =>
      api<CreateSrpResponse>("/api/v1/ecc/srps", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
    },
  });
}

export function useUpdateSrp(srpId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSrpRequest) =>
      api<void>(`/api/v1/ecc/srps/${srpId}`, { method: "PUT", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srp", srpId] });
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
    },
  });
}

export function useCloneSrp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      body,
    }: {
      sourceId: string;
      body: CloneSrpRequest;
    }) =>
      api<CloneSrpResponse>(`/api/v1/ecc/srps/${sourceId}/clone`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "srps"] });
    },
  });
}

export function useDryRunSrp() {
  // No invalidamos cache — dry-run no muta nada en backend.
  return useMutation({
    mutationFn: (body: DryRunSrpRequest) =>
      api<DryRunSrpResponse>("/api/v1/ecc/srps/dry-run", {
        method: "POST",
        body,
      }),
  });
}

// ── Descarga ad-hoc de llamadas por teléfono/extensión ──────────────────────

export function useEccCallSearch() {
  return useMutation({
    mutationFn: (body: EccCallSearchRequest) =>
      api<EccCallJobRef>("/api/v1/ecc/calls/search", { method: "POST", body }),
  });
}

export function useEccCallJob(jobId: string | null) {
  return useQuery({
    queryKey: ["ecc", "call", jobId],
    queryFn: ({ signal }) =>
      api<EccCallJob>(`/api/v1/ecc/calls/${jobId}`, { signal }),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as EccCallJob | undefined;
      if (!data) return POLL_MS;
      // queued (en cola) / searching / downloading son estados de trabajo → seguir polleando.
      // results_ready espera acción del operador; done/failed son terminales.
      const active: string[] = ["queued", "searching", "downloading"];
      return active.includes(data.status) ? POLL_MS : false;
    },
  });
}

export function useEccCallDownload(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EccCallDownloadRequest) =>
      api<EccCallJobRef>(`/api/v1/ecc/calls/${jobId}/download`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "call", jobId] });
    },
  });
}

// Historial global de descargas de llamadas (no por área).
export function useEccCalls(page = 1, perPage = 20) {
  return useQuery({
    queryKey: ["ecc", "calls", page],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      return api<EccCallJobList>(`/api/v1/ecc/calls?${params}`, { signal });
    },
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useDeleteCallJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api<void>(`/api/v1/ecc/calls/${jobId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ecc", "calls"] });
    },
  });
}
